import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';
import PricingBatchDetails from './PricingBatchDetails';
import { labels, livePricingValue, stageSharedPricing, sharedPricingChanges, pricingRuleKey, pricingRuleFields } from '../utils/pricingWorkspace';

const makeSkuId = ({ productCode, expDate }) => {
  const code = String(productCode || 'MKP').replace(/\s+/g, '');
  const exp = expDate ? String(expDate).replaceAll('-', '') : 'NOEXP';
  return `${code}-${exp}`;
};

const VerifyItemInventoryModal = ({ open, item, appliedExtraCharge = 0, onClose, onSave, token, receiptContext }) => {
  const [expDate, setExpDate] = useState('');
  const [mfgDate, setMfgDate] = useState('');
  const [skuId, setSkuId] = useState('');
  const [actualUnitPrice, setActualUnitPrice] = useState('');
  const [unitMrp, setUnitMrp] = useState('');
  const [remarks, setRemarks] = useState('');
  const [pricing,setPricing]=useState({rows:[],scopes:[]});
  const [pricingDrafts,setPricingDrafts]=useState({});
  const [excludedScopes,setExcludedScopes]=useState([]);
  const [pricingPreview,setPricingPreview]=useState(null);
  const [pricingBusy,setPricingBusy]=useState(false);
  const [pricingError,setPricingError]=useState('');
  const [batchId,setBatchId]=useState(null);
  const [pricingReload,setPricingReload]=useState(0);
  const [inputRevision,setInputRevision]=useState(0);
  const [pricingInputs,setPricingInputs]=useState({});
  const pricingEnabled=Boolean(token&&receiptContext);
  const pricingPackId=String(item?.product_barcode_id||item?.product_barcode_id_fk||item?.barcode_id||'');

  useEffect(() => {
    if (!open || !item) return;

    setExpDate(item.exp_date || '');
    setMfgDate(item.mfg_date || '');
    setSkuId(item.sku_id || '');
    setActualUnitPrice(
      item.actual_unit_price ?? item.expected_unit_price ?? item.unit_price ?? ''
    );
    setUnitMrp(item.unit_mrp ?? item.unit_MRP ?? item.mrp ?? item.MRP ?? '');
    setRemarks(item.inventory_remarks || '');
    setPricingDrafts({});setPricingInputs({});setExcludedScopes([]);setPricingPreview(null);setPricingError('');
    setBatchId(item.pricing_publication?.receipt?.batch_id||Date.now());
  }, [open, item]);

  useEffect(()=>{
    if(!open||!pricingEnabled)return;
    if(!pricingPackId){setPricingError('Select a catalog pack before loading prices.');return;}
    const controller=new AbortController();
    let active=true;
    const timeout=setTimeout(()=>controller.abort(),30000);
    setPricingBusy(true);setPricingError('');setPricingPreview(null);setPricing({rows:[],scopes:[]});
    fetch(`${API_BASE_URL}/inventory-pg/pricing-workspace?product_barcode_id=${encodeURIComponent(pricingPackId)}`,{headers:{Authorization:`Bearer ${token}`},signal:controller.signal}).then(async response=>{const data=await response.json();if(!response.ok)throw new Error(data.message||'Could not load existing prices');return data;}).then(data=>{if(active)setPricing(data);}).catch(error=>{if(active)setPricingError(error.name==='AbortError'?'Pricing took too long to load. Retry loading prices.':error.message);}).finally(()=>{clearTimeout(timeout);if(active)setPricingBusy(false);});
    return()=>{active=false;clearTimeout(timeout);controller.abort();};
  },[open,pricingPackId,token,pricingEnabled,pricingReload]);
  useEffect(()=>setPricingPreview(null),[actualUnitPrice,unitMrp,expDate,mfgDate,skuId,remarks,pricingDrafts,excludedScopes]);

  if (!open || !item) return null;

  const units = Number(item.no_of_units ?? 1);
  const extraChargePerUnit = units > 0 ? Number(appliedExtraCharge) / units : null;
  const finalPricePerUnit = extraChargePerUnit === null || actualUnitPrice === ''
    ? null : Number(actualUnitPrice) + extraChargePerUnit;

  const productCode =
    item.product_code || item.productCode || `MKP${item.product_id || item.id}`;

  const autoSku = makeSkuId({
    productCode,
    expDate,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!expDate) {
      alert('Expiry date is required');
      return;
    }

    if (actualUnitPrice === '' || Number(actualUnitPrice) < 0) {
      alert('Actual price/unit is required');
      return;
    }

    if (unitMrp === '' || Number(unitMrp) <= 0) {
      alert('Unit MRP is required');
      return;
    }

    const verified={
      ...item,
      exp_date: expDate,
      mfg_date: mfgDate || null,
      sku_id: skuId || autoSku,
      actual_unit_price: Number(actualUnitPrice),
      unit_mrp: Number(unitMrp),
      inventory_remarks: remarks || null,
      review_pricing_after_receive: false,
      is_verified: true,
    };
    if(pricingEnabled){
      if(pricingBusy||pricingError)return;
      if(Object.values(pricingInputs).some(inputs=>Object.entries(inputs).some(([field,value])=>value===''||!Number.isFinite(Number(value))||Number(value)<0||(field==='factor'&&Number(value)<=0)))){
        setPricingError('Complete all pricing fields with valid values before reviewing.');return;
      }
      if(!pricingPreview){
        const receipt={...receiptContext,purchase_order_item_id:item.is_new?null:item.id,product_barcode_id:item.product_barcode_id||item.product_barcode_id_fk||item.barcode_id,product_id:item.product_id,batch_id:batchId,sku_id:verified.sku_id,qty:Number(item.qty||0),no_of_units:Number(item.no_of_units||1),unit_price:verified.actual_unit_price,unit_mrp:verified.unit_mrp,mfg_date:verified.mfg_date,exp_date:verified.exp_date,remarks:verified.inventory_remarks};
        const request={receipt,entries:Object.values(pricingDrafts),excludedScopes};
        setPricingBusy(true);setPricingError('');
        try{
          const response=await fetch(`${API_BASE_URL}/inventory-pg/pricing-workspace/purchase/preview`,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},body:JSON.stringify(request)});
          const data=await response.json();if(!response.ok)throw new Error(data.message||'Pricing preview failed');
          setPricingPreview({...data,request});
        }catch(error){setPricingError(error.message);}finally{setPricingBusy(false);}
        return;
      }
      verified.pricing_publication={...pricingPreview.request,id:crypto.randomUUID(),fingerprint:pricingPreview.fingerprint};
    }
    onSave(verified);
  };

  const packId=String(item.product_barcode_id||item.product_barcode_id_fk||item.barcode_id||'');
  const productFamily=pricing.rows.find(row=>row.pack===packId)?.family;
  const productRows=pricing.rows.filter(row=>row.pack===packId||(productFamily&&row.family===productFamily));
  const changePricing=(row,field,value)=>{
    setPricingError('');setPricingPreview(null);
    const ruleField=['margin','factor','selling','packingMargin','packingFactor'].includes(field);
    setPricingInputs(all=>{
      const next={...all};
      if(ruleField)for(const peer of productRows.filter(r=>pricingRuleKey(r,field)===pricingRuleKey(row,field))){const inputs={...next[peer.id]};for(const key of pricingRuleFields(field))delete inputs[key];next[peer.id]=inputs;}
      next[row.id]={...next[row.id],[field]:value};return next;
    });
    try{setPricingDrafts(stageSharedPricing(productRows,pricingDrafts,row,field,value));}
    catch(error){setPricingError(error.message);}
  };
  const inputValue=(row,field)=>pricingInputs[row.id]?.[field]??livePricingValue(row,sharedPricingChanges(productRows,pricingDrafts,row),field)??'';

  return (
    <div className="responsive-dialog fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full ${pricingEnabled?'max-w-6xl':'max-w-xl'} max-h-[95vh] overflow-auto rounded-2xl bg-white shadow-xl`}>
        <div className="border-b px-5 py-4">
          <h2 className="text-lg font-bold text-gray-900">
            Verify Product Details
          </h2>
          <p className="text-sm text-gray-500">
            Enter expiry, SKU and actual price/unit before confirming.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div className="rounded-xl border bg-green-50 p-3">
            <div className="font-bold text-gray-900">
              {item.product_name || item.product_id}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              Code: {productCode}
            </div>
            <div className="text-xs text-gray-600">
              MK Barcode: {item.mk_barcode || '-'}
            </div>
            <div className="text-xs text-gray-600">
              Qty: {Number(item.qty || 0).toFixed(3)}
            </div>
            <div className="text-xs text-gray-600">
              No. Units: {Number(item.no_of_units || 1)}
            </div>
            <div className="text-xs text-gray-600">
              Expected Price/Unit: ₹
              {Number(item.expected_unit_price || 0).toFixed(2)}
            </div>
          </div>

          <div className="rounded-xl border bg-emerald-50 p-3 text-sm" aria-label="Applied charges and final price">
            <div>Applied Extra Charge: ₹{Number(appliedExtraCharge).toFixed(2)}</div>
            <p className="text-xs text-gray-600">Includes charges allocated by value and charges applied directly to this item, including GST.</p>
            <div>Extra Charge / Unit: {extraChargePerUnit === null ? 'Unavailable' : `₹${extraChargePerUnit.toFixed(2)}`}</div>
            <div className="font-bold">Final Price / Unit: {finalPricePerUnit === null ? 'Unavailable' : `₹${finalPricePerUnit.toFixed(2)}`}</div>
            <p className="text-xs text-gray-600">Actual price/unit + applied extra charge ÷ number of units.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">
              Actual Price / Unit <span className="text-red-600">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={actualUnitPrice}
              onChange={(e) => setActualUnitPrice(e.target.value)}
              className="w-full rounded-xl border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">
              Unit MRP <span className="text-red-600">*</span>
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={unitMrp}
              onChange={(e) => setUnitMrp(e.target.value)}
              className="w-full rounded-xl border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">MFG Date</label>
            <input
              type="date"
              value={mfgDate || ''}
              onChange={(e) => setMfgDate(e.target.value)}
              className="w-full rounded-xl border px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">
              Expiry Date <span className="text-red-600">*</span>
            </label>
            <input
              type="date"
              value={expDate}
              onChange={(e) => setExpDate(e.target.value)}
              className="w-full rounded-xl border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">SKU ID</label>
            <input
              value={skuId}
              onChange={(e) => setSkuId(e.target.value)}
              placeholder={autoSku}
              className="w-full rounded-xl border px-3 py-2"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to use auto SKU: <b>{autoSku}</b>
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">Remarks</label>
            <input
              value={remarks || ''}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full rounded-xl border px-3 py-2"
              placeholder="Optional"
            />
          </div>

          {pricingEnabled&&<section className="space-y-3 rounded-xl border p-3"><h3 className="font-bold">Existing pack configuration and entity prices</h3><p className="text-sm">This product and brand only, including other pack sizes. Rows sharing a pricing rule update together; the latest edit replaces the earlier edit to that rule. Selling prices recalculate as you type using the displayed purchase + costs, rounded up and capped at MRP. Review the receipt to calculate final prices using incoming stock costs, then confirm this product. Final purchase confirmation saves stock and config and synchronizes included entities.</p>
            {pricingBusy&&<p role="status">Loading pricing…</p>}{pricingError&&<p role="alert" className="text-red-700">{pricingError}<button type="button" className="ml-3 underline" onClick={()=>setPricingReload(value=>value+1)}>Reload pricing and retry</button></p>}
            <div className="overflow-x-auto"><table className="min-w-[1000px] w-full text-sm"><thead><tr><th>Pack</th><th>Entity / location</th><th>Purchase + costs</th>{['packingMargin','margin','factor','selling','mrp'].map(field=><th key={field}>{labels[field]}</th>)}</tr></thead><tbody>{productRows.map(row=><tr key={`${row.id}:${inputRevision}`} className="border-t"><td className="p-2">{row.size}<div className="text-xs">{row.barcode}</div><PricingBatchDetails row={row} changes={sharedPricingChanges(productRows,pricingDrafts,row)}/></td><td className="p-2">{pricing.scopes.find(s=>s.id===row.scope)?.name||row.scope}</td><td className="p-2">{row.cost??'—'}</td>{['packingMargin','margin','factor','selling','mrp'].map(field=><td key={field} className="p-2">{row.editable.includes(field)?<input aria-label={`${row.id} ${labels[field]}`} className="w-24 rounded border p-1" type="number" min="0" step="any" disabled={pricingBusy} value={inputValue(row,field)} onChange={e=>changePricing(row,field,e.target.value)}/>:row[field]??'—'}</td>)}</tr>)}</tbody></table></div>
            {!pricingBusy&&!productRows.length&&<p>No existing inventory prices for this product. The preview will calculate from the incoming pack and applicable configuration.</p>}
            <fieldset><legend className="font-semibold">Entities to update</legend>{pricing.scopes.filter(s=>s.type!=='warehouse').map(scope=><label key={scope.id} className="inline-flex gap-2 mr-4"><input type="checkbox" checked={!excludedScopes.includes(scope.id)} disabled={pricingBusy||Object.values(pricingDrafts).some(d=>d.scope===scope.id)} onChange={e=>{setPricingError('');setExcludedScopes(ids=>e.target.checked?ids.filter(id=>id!==scope.id):[...ids,scope.id]);}}/>{scope.name}</label>)}</fieldset>
            <button type="button" className="rounded border px-3 py-2" disabled={pricingBusy||!Object.keys(pricingDrafts).length} onClick={()=>{setPricingDrafts({});setPricingInputs({});setPricingPreview(null);setPricingError('');setInputRevision(value=>value+1);}}>Clear pricing drafts</button>
            {pricingPreview&&<div className="rounded bg-blue-50 p-3"><h4 className="font-bold">Review calculated changes</h4><p className="text-sm">Incoming stock: {units} packs. Purchase/unit: ₹{actualUnitPrice}. MRP: ₹{unitMrp}.</p>{pricingPreview.effects.map((effect,index)=><div key={index} className="border-t py-2"><b>{effect.name} · {effect.size} · {pricing.scopes.find(s=>s.id===effect.scope)?.name||effect.scope}</b>{effect.excluded?<p>Excluded from this publication</p>:effect.changes.map(change=><p key={change.field}>{labels[change.field]||change.field}: {change.before??'—'} → {change.after??'—'}</p>)}</div>)}{pricingPreview.warnings.map(w=><p className="text-xs mt-1" key={w}>{w}</p>)}</div>}
          </section>}
          <div className="flex flex-wrap justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border px-4 py-2 font-semibold"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={pricingEnabled&&(pricingBusy||Boolean(pricingError))}
              className="rounded-xl bg-green-700 px-4 py-2 font-semibold text-white"
            >
              {pricingEnabled&&!pricingPreview?'Review Product & Prices':'Confirm Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyItemInventoryModal;
